/** Where to go after login: a route of this app, or a full page load (e.g. the CRM under /crm/). */
export type LoginRedirectTarget = { type: 'route'; path: string } | { type: 'page'; url: string };

const HOME: LoginRedirectTarget = { type: 'route', path: '/' };

/**
 * Turns the `?redirect=` value of a login page into a safe target. Only a root-relative path
 * or an absolute URL on this app's own origin passes; anything else goes home, so the login
 * page can never send a freshly logged-in user to a foreign site.
 */
export function resolveLoginRedirect(redirect: string | null, currentOrigin: string): LoginRedirectTarget {
  const isRootRelativePath = redirect?.startsWith('/') ?? false;
  const isAbsoluteHttpUrl = /^https?:\/\//i.test(redirect ?? '');
  if (!redirect || (!isRootRelativePath && !isAbsoluteHttpUrl)) {
    return HOME;
  }

  // Judge the URL as the browser will resolve it: it reads "/\host" and "/<tab>/host" as "//host"
  const target = parseUrl(redirect, currentOrigin);
  if (target?.origin !== currentOrigin) {
    return HOME;
  }

  if (isRootRelativePath) {
    return { type: 'route', path: target.pathname + target.search + target.hash };
  }
  return { type: 'page', url: target.href };
}

/**
 * Sends a freshly logged-in user on to the login page's validated `?redirect=` target.
 * A target outside the app router, such as the CRM, needs a full page load.
 */
export function followLoginRedirect(
  location: Pick<Location, 'search' | 'origin' | 'assign'>,
  navigateToPath: (path: string) => void
): void {
  const redirect = new URLSearchParams(location.search).get('redirect');
  const target = resolveLoginRedirect(redirect, location.origin);

  if (target.type === 'page') {
    location.assign(target.url);
    return;
  }
  navigateToPath(target.path);
}

function parseUrl(url: string, base: string): URL | null {
  try {
    return new URL(url, base);
  } catch {
    return null;
  }
}
